import React, { useEffect, useState } from "react";
import { planningCenterToolsStatus, useStatuses } from "../../selectors/status";
import { fetchPlanningCenterTools } from "../../reducers/planning-center-overview/planning-center-tools";
import { fetchViewPlanningCenterDocument } from "../../reducers/view-planning-center-docuement/view-planning-center-document";
import { useAppDispatch, useSelector } from "../../store/store";
import { Page, PageHeader } from "src/components/page";
import { AsyncStatus } from "src/models/async-status";
import { ToolLink } from "src/models/plan-info-and-summary/tool-link";
import {
  buildLinkForEstimatedBenefitCalculator,
  buildLinkForPlanInfoAndFormPage,
  buildLinkForPlanOverviewPage,
} from "src/util/nq-participant-link-builder";

export default function PlanningCenterPage() {
  const { planningCenterTools } = useSelector(
    (state) => state.planningCenterTools
  );
  const { viewPlanningCenterDocument } = useSelector(
    (state) => state.viewPlanningCenterDocument
  );
  const { planId, empId, role } = useSelector((state) => state.session);
  const { isLoadingStatus } = useStatuses([planningCenterToolsStatus]);
  const [currentToolLink, setCurrentToolLink] = useState("");
  const [error, setError] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchPlanningCenterTools({ planId, empId, role }));
    if (
      viewPlanningCenterDocument &&
      viewPlanningCenterDocument.status === AsyncStatus.error
    ) {
      setError(true);
    }
  }, [dispatch, planId, empId, role, viewPlanningCenterDocument]);

  const viewPlanningCenterDocuments = (
    extension: string,
    resourceId: number,
    toolLinkName: string
  ) => {
    dispatch(
      fetchViewPlanningCenterDocument({
        planId,
        empId,
        extension,
        resourceId,
        role,
      })
    );
    setCurrentToolLink(toolLinkName);
  };

  const planOverviewUrl = () => {
    return buildLinkForPlanOverviewPage(planId, empId, role);
  };

  const isBenefitCalc = (toolLink: ToolLink) => {
    return toolLink.linkName === "Estimated Benefit Calculator";
  };

  const nonQualifiedPlanner = (toolLink: ToolLink) => {
    return toolLink.linkName === "Nonqualified Deferred Compensation Planner";
  };

  const isPlanInfoAndForm = (tooLink: ToolLink) => {
    return tooLink.linkName === "Plan Info & Forms";
  };

  const isClickableLink = (toolLink: ToolLink) => {
    setError(false);
    return (
      isBenefitCalc(toolLink) ||
      nonQualifiedPlanner(toolLink) ||
      isPlanInfoAndForm(toolLink) ||
      !!toolLink.outputLink
    );
  };

  const getUrl = (toolLink: ToolLink) => {
    if (isBenefitCalc(toolLink)) {
      return buildLinkForEstimatedBenefitCalculator(planId, empId, role);
    } else if (nonQualifiedPlanner(toolLink)) {
      return "https://nq.individuals.principal.com/nq-compensation-planner/#/";
    } else if (isPlanInfoAndForm(toolLink)) {
      return buildLinkForPlanInfoAndFormPage(planId, empId, role);
    } else if (!!toolLink.outputLink) {
      return toolLink.urlLink;
    }
    return "#";
  };

  const getTarget = (toolLink: ToolLink) => {
    if (nonQualifiedPlanner(toolLink)) {
      return "_blank";
    }
    return "_self";
  };

  return (
    <Page name={"planning-center-tools"} loading={isLoadingStatus}>
      {
        <span>
          To return to Your Plan Overview, please{" "}
          <a href={planOverviewUrl()}>Click Here.</a>
        </span>
      }
      {viewPlanningCenterDocument.status === AsyncStatus.error && error ? (
        <div className="alert alert-danger alert-icon" role="alert">
          {currentToolLink} is unavailable at this time. Please call for
          assistance.
        </div>
      ) : (
        ""
      )}
      <PageHeader name={"planning-center-tools"}>
        Planning Center Overview
      </PageHeader>

      {
        <span>
          The following resources are available for your review. Some of these
          resources are in PDF format. In order to view a PDF file, you must
          have Adobe Acrobat Reader installed on your computer.
        </span>
      }
      <ul>
        {planningCenterTools.toolLinks.map((toolLink) => (
          <li className="util-margin-top-15">
            <a
              href={getUrl(toolLink)}
              onClick={() =>
                isClickableLink(toolLink)
                  ? true
                  : viewPlanningCenterDocuments(
                      toolLink.extension,
                      toolLink.resourceId,
                      toolLink.linkName
                    )
              }
              target={getTarget(toolLink)}
            >
              {toolLink.linkName}
            </a>
            &nbsp;&nbsp;<span>{toolLink.extension}</span>
            <div>
              <span>{toolLink.linkDescription}</span>
            </div>
          </li>
        ))}
      </ul>
      <div className="util-margin-top-15">
        <span>
          The amounts listed on the website represent a measure of your benefits
          under a nonqualified benefit agreement between you and your plan
          sponsor. It is not a representation that your plan sponsor holds any
          assets for paying these benefits.
        </span>

        <div className="util-margin-top-15">
          These resources are for general information only and are not intended
          to provide specific advice or recommendations to any individual. All
          situations and investors described are hypothetical. Please consult
          your attorney, accountant or financial or tax advisor with regard to
          your individual situation.
        </div>
      </div>
    </Page>
  );
}
